import { MAX_NUMBER_OF_DAYS_FOR_FORECAST } from "@Constants/forecast";
import { REPRESENTATIONS, Representation } from "@Constants/representation";
import {
  useForecastAppContext,
  useForecastAppDispatcher,
} from "@Context/ForecastAppContext/ForecastAppContext";
import { formatAsIsoStyleDate } from "@Utils/date";
import { animateScrollToPosition } from "@Utils/scrolling";
import { trackDaySelection } from "@Utils/tracking";
import {
  RefObject,
  SyntheticEvent,
  createRef,
  useEffect,
  useRef,
  useState,
} from "react";
import { useDebounceCallback } from "usehooks-ts";

const SCROLL_HANDLER_DEBOUNCE_TIME = 100;
const MOUSE_WHEEL_SCROLL_HANDLER_DEBOUNCE_TIME = 200;
const SCROLL_STEP = 600;
const NAVIGATE_DEBOUNCE_TIME = 50;

export type ScrollHandler = (
  e: SyntheticEvent<HTMLElement>,
  source: RefObject<HTMLDivElement | null> | null,
) => void;

type UseScrollInteractions = {
  containerRefs: React.RefObject<
    Record<Representation, RefObject<HTMLDivElement | null>>
  >;
  dayRefs: React.RefObject<
    Record<Representation, RefObject<HTMLLIElement | null>[]>
  >;
  canNavigateLeft: boolean;
  canNavigateRight: boolean;
  navigateLeft: () => void;
  navigateRight: () => void;
  handleScroll: ScrollHandler;
  handleMouseWheelScroll: ScrollHandler;
};

export const useScrollInteractions = (): UseScrollInteractions => {
  const dispatch = useForecastAppDispatcher();
  const { activeDate, forecastPointWeek } = useForecastAppContext();
  const [scrollXPosition, setScrollXPosition] = useState<number | null>(null);
  const previousActiveDate = useRef<string | undefined>(undefined);
  const animateScroll = useRef<boolean>(true);
  const checkForScrollPositionCrossedDateBoundary = useRef<boolean>(true);
  const containerRefs = useRef<
    Record<Representation, RefObject<HTMLDivElement | null>>
  >({
    temperature: createRef<HTMLDivElement | null>(),
    wind: createRef<HTMLDivElement | null>(),
    snow: createRef<HTMLDivElement | null>(),
  });

  const createRepresentationRefs = () => {
    return Array.from({ length: MAX_NUMBER_OF_DAYS_FOR_FORECAST }, () =>
      createRef<HTMLLIElement | null>(),
    );
  };
  const dayRefs = useRef<
    Record<Representation, RefObject<HTMLLIElement | null>[]>
  >({
    temperature: createRepresentationRefs(),
    wind: createRepresentationRefs(),
    snow: createRepresentationRefs(),
  });
  const activeNavigationContainerRef = useRef<
    RefObject<HTMLLIElement | null>[] | null
  >(null);
  const dayIndexLookup = useRef<Map<string, number>>(new Map());
  const [canNavigateLeft, setCanNavigateLeft] = useState<boolean>(true);
  const [canNavigateRight, setCanNavigateRight] = useState<boolean>(true);
  const getMaxScrollablePosition = () => {
    const containerElement = containerRefs.current[REPRESENTATIONS[0]].current;
    if (!containerElement) {
      return 0;
    }

    const maxContainerScrollWidth = containerElement.scrollWidth;
    const maxScrollablePosition =
      maxContainerScrollWidth - containerElement.getBoundingClientRect().width;

    return maxScrollablePosition;
  };

  const getActiveDateFromScrollPosition = () => {
    let referencePoint = window.innerWidth / 2; // center of screen

    // On larger screens it is not possible to scroll the last day past the end of the screen,
    // thus we "bias" the reference point a bit to the right
    if (window.innerWidth >= 1024) {
      const bias =
        dayRefs.current[REPRESENTATIONS[0]].length > 0
          ? (dayRefs.current[
              REPRESENTATIONS[0]
            ][0]?.current?.getBoundingClientRect().width ?? 0) * 2
          : 0;
      referencePoint = referencePoint + bias;
    }

    if (!activeNavigationContainerRef.current) {
      return;
    }

    const daysLeftOfReferencePoint = activeNavigationContainerRef?.current
      // remove elements right from reference point
      .filter((activeNavigationContainerRef) => {
        if (!activeNavigationContainerRef.current) {
          return false;
        }
        const dayElement = activeNavigationContainerRef.current;
        return dayElement.getBoundingClientRect().left <= referencePoint;
      })
      // sort by distance to reference point, closest first
      .sort((a, b) => {
        const ax = a.current?.getBoundingClientRect().left ?? 0;
        const bx = b.current?.getBoundingClientRect().left ?? 0;

        if (ax === bx) {
          return 0;
        }

        return ax < bx ? 1 : -1;
      });

    if (
      daysLeftOfReferencePoint.length === 0 ||
      !daysLeftOfReferencePoint[0].current
    ) {
      return undefined;
    }

    return daysLeftOfReferencePoint[0].current.dataset.day;
  };

  const updateScrollXPosition = (
    position: number | ((previousPosition: number | null) => number),
    skipCheckForScrollPositionCrossedDateBoundary = false,
  ) => {
    checkForScrollPositionCrossedDateBoundary.current =
      !skipCheckForScrollPositionCrossedDateBoundary;
    setScrollXPosition(position);
  };

  const navigateLeft = useDebounceCallback(() => {
    animateScroll.current = true;

    updateScrollXPosition((previousScrollXPosition) =>
      Math.max((previousScrollXPosition ?? 0) - SCROLL_STEP, 0),
    );
  }, NAVIGATE_DEBOUNCE_TIME);

  const navigateRight = useDebounceCallback(() => {
    animateScroll.current = true;

    updateScrollXPosition((previousScrollXPosition) =>
      Math.min(
        (previousScrollXPosition ?? 0) + SCROLL_STEP,
        getMaxScrollablePosition(),
      ),
    );
  }, NAVIGATE_DEBOUNCE_TIME);

  const innerHandleScroll = (
    _e: SyntheticEvent<HTMLElement>,
    sourceRef: RefObject<HTMLDivElement | null> | null,
  ) => {
    if (!sourceRef || !sourceRef.current) {
      return;
    }

    const sourceElement = sourceRef.current;
    const scrollPosition = sourceElement.scrollLeft;

    animateScroll.current = true;

    updateScrollXPosition(scrollPosition);
  };

  // Wait for the scroll event to finish before updating the scroll position
  const handleScroll = (
    _e: SyntheticEvent<HTMLElement>,
    sourceRef: RefObject<HTMLDivElement | null> | null,
  ) => {
    if (!sourceRef || !sourceRef.current) {
      return;
    }

    let previousPosition = 0;
    const handler = setInterval(() => {
      if (!sourceRef || !sourceRef.current) {
        return;
      }

      const sourceElement = sourceRef.current;
      const position = sourceElement.scrollLeft;

      if (position === previousPosition) {
        // Scrolling has stopped
        clearInterval(handler);
        innerHandleScroll(_e, sourceRef);
      }
      previousPosition = position;
    }, SCROLL_HANDLER_DEBOUNCE_TIME);
  };

  const handleMouseWheelScroll = useDebounceCallback(
    innerHandleScroll,
    MOUSE_WHEEL_SCROLL_HANDLER_DEBOUNCE_TIME,
  );

  const scrollToDate = (date: string) => {
    const dayIndex = dayIndexLookup.current.get(date) ?? 0;
    const dayRef = dayRefs.current[REPRESENTATIONS[0]][dayIndex];
    if (!dayRef || !dayRef.current) {
      return;
    }

    const dayElement = dayRef.current;
    updateScrollXPosition(
      Math.min(dayElement.offsetLeft, getMaxScrollablePosition()),
      true,
    );
  };

  const scrollContainersToPosition = (position: number) => {
    REPRESENTATIONS.forEach((representation) => {
      const containerElement = containerRefs.current[representation]?.current;
      if (!containerElement) {
        return;
      }

      animateScrollToPosition(
        containerElement,
        position,
        animateScroll.current ?? true,
      );
    });
  };

  const trackActiveContainer =
    (representation: Representation) => (e: Event) => {
      const containerElement = containerRefs?.current[representation]?.current;
      if (e.target === containerElement) {
        activeNavigationContainerRef.current = dayRefs.current[representation];
      }
    };

  useEffect(() => {
    const scrollBindings = REPRESENTATIONS.map((representation) => ({
      element: containerRefs?.current[representation]?.current,
      listener: trackActiveContainer(representation),
    }));

    scrollBindings.forEach(({ element, listener }) => {
      element?.addEventListener("scroll", listener);
    });

    return () => {
      scrollBindings.forEach(({ element, listener }) => {
        element?.removeEventListener("scroll", listener);
      });
    };
  }, []);

  // Listen for scroll position updates, and run the check if the date needs to be switched, if necessary.
  useEffect(() => {
    if (scrollXPosition === null || dayIndexLookup.current.size === 0) {
      return;
    }
    scrollContainersToPosition(scrollXPosition);
    setCanNavigateLeft(scrollXPosition > 0);
    setCanNavigateRight(
      scrollXPosition === 0 || scrollXPosition < getMaxScrollablePosition(),
    );

    animateScroll.current = true;

    // Check whether we need to change the active date in case the user
    // has scrolled past a day boundary
    if (checkForScrollPositionCrossedDateBoundary.current) {
      const dateFromScrollPosition = getActiveDateFromScrollPosition();
      if (
        previousActiveDate.current !== dateFromScrollPosition &&
        dateFromScrollPosition
      ) {
        const dateIndex =
          dayIndexLookup.current.get(dateFromScrollPosition) ?? 0;
        trackDaySelection(dateIndex, "scroll");

        dispatch({
          type: "set-active-date",
          data: {
            value: dateFromScrollPosition,
            source: "scroll-over-date-boundary",
          },
        });
      }
    }
  }, [dispatch, scrollXPosition]);

  // Listen for changes of the active date, and scroll to the correct date. Also account for the source of the change, to skip scrolling at
  // all or disable the animation.
  useEffect(() => {
    previousActiveDate.current = activeDate.value;

    // For the initial load, disable the animation, so the correct date appears instantly
    if (activeDate.source === "initial") {
      animateScroll.current = false;
    } else {
      animateScroll.current = true;
    }

    // Do not scroll if the date was changed due to scrolling over a date boundary
    if (activeDate.source === "scroll-over-date-boundary") {
      return;
    }

    if (
      activeDate &&
      activeDate.value &&
      Object.keys(containerRefs.current).length > 0 &&
      Object.keys(dayRefs.current[REPRESENTATIONS[0]]).length > 0 &&
      dayIndexLookup.current.size > 0
    ) {
      scrollToDate(activeDate.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeDate,
    containerRefs.current,
    dayRefs.current,
    dayIndexLookup.current.size,
  ]);

  useEffect(() => {
    if (!forecastPointWeek) {
      return;
    }

    // Construct a map of day dates to their index dayRefs array
    const { days = [] } = forecastPointWeek;
    const dayDates = days
      .map((day) => formatAsIsoStyleDate(day.date_time))
      .sort();

    dayIndexLookup.current.clear();
    dayDates.forEach((dayDate, index) => {
      dayIndexLookup.current.set(dayDate, index);
    });
  }, [forecastPointWeek]);

  return {
    containerRefs,
    dayRefs,
    canNavigateLeft,
    canNavigateRight,
    navigateLeft,
    navigateRight,
    handleScroll,
    handleMouseWheelScroll,
  };
};
